import { type RefObject, useCallback, useEffect, useId, useRef } from 'react';
import classNames from 'classnames';
import styles from './Modal.module.scss';
import type { ModalProps, ModalRef } from './Modal.data';
import ModalCloseButton from './ModalCloseButton';
import type { IconButtonRef } from '../../atoms/icon-button/IconButton.data';
import { useTransitionController } from '../../../util/react-transition-component';
import { setupTransitionInTimeline, setupTransitionOutTimeline } from './Modal.transitions';

const Modal = ({
  ref,
  children,
  open,
  onClose,
  onCloseStart,
  onShow,
  title,
  className,
  backgroundClassName,
}: ModalProps) => {
  const localRef = useRef(null);
  const elementRef = (ref || localRef) as RefObject<ModalRef | null>;
  const prevOpen = useRef(open);
  const closeButtonRef = useRef<IconButtonRef>(null);
  const backgroundRef = useRef<HTMLDivElement>(null);
  const id = useId();

  const transitionController = useTransitionController(
    () => ({
      ref: elementRef,
      refs: {
        elementRef,
        closeButtonRef,
        backgroundRef,
      },
      setupTransitionInTimeline,
      setupTransitionOutTimeline,
    }),
    [],
  );

  const handleClose = useCallback(async () => {
    if (onCloseStart) onCloseStart();
    await transitionController.transitionOut();
    if (onClose) onClose();
  }, [onClose, onCloseStart, transitionController]);

  const handleOpen = useCallback(async () => {
    await transitionController.transitionIn();
    if (onShow) onShow();
  }, [onShow, transitionController]);

  useEffect(() => {
    function handleKeyUp(event: KeyboardEvent) {
      const key = event.code;

      if (key === 'Escape') handleClose();
    }

    if (!prevOpen.current && open) {
      elementRef.current?.showModal();
      document.addEventListener('keyup', handleKeyUp);
      handleOpen();
    } else if (prevOpen.current && !open) {
      elementRef.current?.close();
      document.removeEventListener('keyup', handleKeyUp);
    }
    prevOpen.current = open;

    return () => {
      document.removeEventListener('keyup', handleKeyUp);
    };
  }, [open, onClose, elementRef, handleClose, handleOpen]);

  return (
    <>
      <dialog
        ref={elementRef}
        className={classNames(styles.modal, className, {
          [styles.isVisible]: open,
        })}
        aria-labelledby={`modal_label_${id}`}
        aria-modal="true"
        aria-hidden={!open ? 'true' : 'false'}
      >
        {title && (
          <h2 id={`modal_label_${id}`} className="visually-hidden">
            {title}
          </h2>
        )}
        {children}

        <ModalCloseButton ref={closeButtonRef} onClose={handleClose} />
      </dialog>
      <div className={classNames(styles.background, backgroundClassName)} ref={backgroundRef} />
    </>
  );
};

export default Modal;
